import React from 'react';
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores';
import type { AppliedFilterChip } from '@/utils/inventory';

export interface AppliedFiltersStripProps {
  count: number;
  chips: AppliedFilterChip[];
  onChipClick: (filterId: string) => void;
  onChipRemove: (filterId: string) => void;
  onClearAll: () => void;
  /** When true, do not render the trigger row (trigger is rendered elsewhere, e.g. in page header). */
  hideTrigger?: boolean;
  /** Controlled expanded state. When provided, the component uses these instead of the global UI store. */
  expanded?: boolean;
  onToggleExpanded?: (expanded: boolean) => void;
}

export const AppliedFiltersStrip: React.FC<AppliedFiltersStripProps> = ({
  count,
  chips,
  onChipClick,
  onChipRemove,
  onClearAll,
  hideTrigger = false,
  expanded: controlledExpanded,
  onToggleExpanded,
}) => {
  const storeExpanded = useUIStore((state) => state.appliedFiltersExpanded);
  const storeSetExpanded = useUIStore((state) => state.setAppliedFiltersExpanded);
  const appliedFiltersExpanded = controlledExpanded !== undefined ? controlledExpanded : storeExpanded;
  const setAppliedFiltersExpanded = onToggleExpanded ?? storeSetExpanded;

  if (count === 0) return null;

  const handleChipClick = (filterId: string): void => {
    onChipClick(filterId);
  };

  const handleChipRemove = (e: React.MouseEvent, filterId: string): void => {
    e.stopPropagation();
    onChipRemove(filterId);
  };

  const showClearAll = count > 1;
  const expandableContent = appliedFiltersExpanded && (
        <div className="px-4 pb-3 pt-0 flex flex-wrap items-center justify-end gap-2">
          {chips.map((chip) => (
            <div
              key={chip.filterId}
              className="inline-flex items-center gap-1.5 rounded-md border border-blue-600 px-1.5 py-0.5 text-xs font-medium text-blue-600"
            >
              {chip.clickable === false ? (
                // No click target (the filter's UI lives outside the sidebar) —
                // a hover-styled button here would promise an action that never
                // happens (LR-474 r3 #2). The X-remove stays interactive.
                <span className="px-0.5 py-0.5 text-left">{chip.label}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleChipClick(chip.filterId)}
                  className="rounded px-0.5 py-0.5 text-left hover:bg-blue-50"
                >
                  {chip.label}
                </button>
              )}
              <button
                type="button"
                onClick={(e) => handleChipRemove(e, chip.filterId)}
                className="shrink-0 rounded p-0.5 hover:bg-blue-100 text-blue-600 focus:outline-none"
                aria-label={`Remove ${chip.label} filter`}
              >
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          ))}
          {showClearAll && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClearAll();
                setAppliedFiltersExpanded(false);
              }}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
              Clear all
            </button>
          )}
        </div>
  );

  if (hideTrigger) {
    if (!appliedFiltersExpanded) return null;
    return (
      <div className="flex flex-col items-end bg-white pt-2">
        {expandableContent}
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white">
      <div
        className="flex items-center justify-between gap-2 px-4 py-2 min-h-[2.25rem]"
        role="button"
        tabIndex={0}
        onClick={() => setAppliedFiltersExpanded(!appliedFiltersExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setAppliedFiltersExpanded(!appliedFiltersExpanded);
          }
        }}
        aria-expanded={appliedFiltersExpanded}
        aria-label="Applied filters"
      >
        <span className="text-sm font-medium text-gray-700">
          Applied filters: {count}
        </span>
        <span className="flex items-center gap-1 text-gray-500">
          {appliedFiltersExpanded ? (
            <ChevronUpIcon className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronDownIcon className="h-4 w-4" aria-hidden />
          )}
        </span>
      </div>
      {expandableContent}
    </div>
  );
};
